import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  finishImpersonation,
  getImpersonationToken,
  ImpersonationError,
} from "@/lib/api/impersonation-client";
import {
  clearImpersonationSession,
  getImpersonationSession,
  IMPERSONATION_CHANGE_EVENT,
  type ImpersonationSession,
} from "@/lib/impersonation/session";
import { isTokenExpired } from "@/lib/types/jwt";

/**
 * Fixed banner shown in the SPA while an admin is impersonating a customer on the
 * headless storefront.
 *
 * Three states, derived from storage + the impersonation JWT's exp claim:
 *  - Active:   amber banner "Impersonating {name} — Finish session".
 *  - Error:    brief red flash after a failed Finish attempt.
 *  - Expired:  neutral banner "Impersonation session expired — Return to admin".
 *              No /login redirect is performed (the shared ApiClient's 401
 *              expired-user path is bypassed for impersonation tokens — see
 *              token-manager.ts).
 *
 * Finish behavior: POST /api-bf/Impersonation/Finish with the impersonation token,
 * then a full-page navigation to {adminBaseUrl}Admin/Customer/Edit/{targetId}
 * (the admin's nop auth cookie is still valid there). Only the banner's Finish
 * button can end the session from the storefront.
 */
export function ImpersonationBanner() {
  const [session, setSession] = useState<ImpersonationSession | null>(() =>
    getImpersonationSession(),
  );
  const [tokenExpired, setTokenExpired] = useState<boolean>(() =>
    isTokenExpired(getImpersonationToken() ?? ""),
  );
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sync = () => {
      setSession(getImpersonationSession());
      setTokenExpired(isTokenExpired(getImpersonationToken() ?? ""));
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener(IMPERSONATION_CHANGE_EVENT, sync);
    // Re-check periodically: token expiry flips the banner to the expired state
    // without needing an API failure to trigger a re-render.
    const interval = window.setInterval(sync, 30_000);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("storage", sync);
      window.removeEventListener(IMPERSONATION_CHANGE_EVENT, sync);
    };
  }, []);

  // Publish the banner's measured height as a CSS var so the sticky navbar can
  // park below it without overlap (see navbar.tsx). Cleared to 0px on unmount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const update = () => {
      const h = bannerRef.current?.offsetHeight ?? 0;
      root.style.setProperty("--imp-banner-height", `${h}px`);
    };
    update();
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined" && bannerRef.current) {
      observer = new ResizeObserver(update);
      observer.observe(bannerRef.current);
    }
    // ResizeObserver on the element also covers text wrapping on narrow viewports.
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      root.style.setProperty("--imp-banner-height", "0px");
    };
  }, [session, error]);

  const finish = useCallback(async () => {
    const token = getImpersonationToken();
    const current = getImpersonationSession();
    if (!token || !current) return;

    setFinishing(true);
    setError(null);
    try {
      const response = await finishImpersonation(token);
      clearImpersonationSession();

      // Navigate the top-level window back to the admin area on the host the admin
      // started from (signed into the bridge payload; paths containing "admin" are
      // exempt from the injected anti-headless redirect script).
      const base = (response.admin_base_url || current.adminBaseUrl || "").replace(/\/?$/, "/");
      if (base) {
        window.location.href = `${base}Admin/Customer/Edit/${current.targetCustomerId}`;
      } else {
        // No host recorded (shouldn't happen with the bridge flow) — fall back to
        // the backend origin's admin area rather than stranding the admin here.
        const backendHost = `https://${import.meta.env.VITE_BACKEND_CUSTOM_DOMAIN_NAME}`;
        window.location.href = `${backendHost}/Admin/Customer/Edit/${current.targetCustomerId}`;
      }
    } catch (e) {
      setFinishing(false);
      if (e instanceof ImpersonationError) {
        setError(e.message);
      } else {
        console.error("Finish impersonation failed:", e);
        setError("Failed to finish the impersonation session. Please try again.");
      }
    }
  }, []);

  /** Return the admin to their admin area without server-side cleanup. Used in the expired state. */
  const returnToAdmin = useCallback(() => {
    const current = getImpersonationSession();
    clearImpersonationSession();
    // Navigate to the admin customer-edit path, NOT the admin host root: the
    // injected anti-headless script treats most nop-host paths as redirect-bait,
    // but exempts paths containing "admin". Landing on the edit page also puts
    // the admin exactly where they want to resume.
    const base = (current?.adminBaseUrl || "").replace(/\/?$/, "/");
    const targetId = current?.targetCustomerId;
    if (base && targetId) {
      window.location.href = `${base}Admin/Customer/Edit/${targetId}`;
    } else {
      // Unknown admin host — best effort: send them to the admin area of the
      // backend origin so at minimum the script-exempt /admin path is used.
      const backendHost = `https://${import.meta.env.VITE_BACKEND_CUSTOM_DOMAIN_NAME}`;
      window.location.href = targetId
        ? `${backendHost}/Admin/Customer/Edit/${targetId}`
        : `${backendHost}/Admin`;
    }
  }, []);

  if (!session) return null;

  const expired = tokenExpired || Date.now() / 1000 >= session.expiresAt;

  if (expired) {
    return (
      <div
        ref={bannerRef}
        role="status"
        data-impersonation-banner
        className="sticky top-0 z-[60] w-full -mb-px bg-muted text-foreground border-b border-border shadow-sm"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2">
          <p className="text-sm">
            <span className="font-medium">Impersonation session expired.</span>{" "}
            <span className="text-muted-foreground">API access as {session.targetEmail} is no longer available.</span>
          </p>
          <button
            type="button"
            onClick={returnToAdmin}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-secondary hover:bg-primary/90 shrink-0"
          >
            Return to admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Impersonation session"
      data-impersonation-banner
      className="sticky top-0 z-[60] w-full -mb-px bg-amber-500 text-amber-950 shadow-sm"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2">
        <p className="text-sm">
          <span className="font-semibold">
            {error ? "Impersonation" : `Impersonating ${session.targetDisplayName}`}
          </span>
          {error ? (
            <span className="ml-2">{error}</span>
          ) : (
            <span className="ml-2 opacity-90">({session.targetEmail}) — actions here affect the customer's account.</span>
          )}
        </p>
        <button
          type="button"
          onClick={finish}
          disabled={finishing}
          className="rounded-md bg-amber-950 px-3 py-1.5 text-sm font-medium text-amber-50 hover:bg-amber-900 disabled:opacity-60 shrink-0"
        >
          {finishing ? "Finishing…" : "Finish session"}
        </button>
      </div>
    </div>
  );
}

/**
 * True when the app is currently in an active impersonation session. Shared
 * helper for guards (e.g. suppressing the SPA logout while impersonating).
 */
export const impersonationInProgress = (): boolean => {
  const current = getImpersonationSession();
  return !!current && !isTokenExpired(getImpersonationToken() ?? "") && Date.now() / 1000 < current.expiresAt;
};

export default ImpersonationBanner;